import Link from 'next/link';

import { formatMoney } from '@/shared/lib/money';
import { Badge, MediaTag } from '@/shared/ui/badge';
import { Chip } from '@/shared/ui/chip';

import { productHref } from '../lib/href';
import { availabilityBadge, primaryMedia } from '../lib/variants';
import { AVAILABILITY_LABELS, DIRECTION_LABELS, OCCASION_LABELS } from '../model/labels';
import type { Product } from '../model/product';
import styles from './product-card.module.scss';
import { ProductMedia } from './product-media';

export type ProductCardProps = {
  product: Product;
  showDemoTags?: boolean;
  priority?: boolean;
  /** "catalog" — 460px photo, chip and price; "compact" — hatched tile with name and price. */
  variant?: 'catalog' | 'compact';
  className?: string | undefined;
};

export function ProductCard({
  product,
  showDemoTags = false,
  priority = false,
  variant = 'catalog',
  className,
}: ProductCardProps) {
  const media = primaryMedia(product);
  const occasion = product.occasions[0];
  const badge =
    product.badge ?? (product.price ? AVAILABILITY_LABELS[availabilityBadge(product)] : 'Скоро');
  if (variant === 'compact') {
    return (
      <Link
        href={productHref(product)}
        className={[styles.compact, className].filter(Boolean).join(' ')}
      >
        <ProductMedia
          media={media}
          fallbackLabel={product.shortLabel}
          sizes="(min-width: 1024px) 20vw, 60vw"
          tint={product.tint}
          className={styles.thumb}
        />
        <span className={styles.title}>{product.name}</span>
        <span className={styles.cost}>
          {product.price ? formatMoney(product.price) : 'Цена уточняется'}
        </span>
      </Link>
    );
  }
  return (
    <Link
      href={productHref(product)}
      className={[styles.card, className].filter(Boolean).join(' ')}
    >
      <ProductMedia
        media={media}
        fallbackLabel={product.shortLabel}
        priority={priority}
        sizes="(min-width: 1280px) 20rem, (min-width: 768px) 30vw, 50vw"
        tint={media?.src ? null : product.tint}
        className={styles.media}
      >
        <Badge>{badge}</Badge>
        {showDemoTags && media?.demoTag && <MediaTag>{media.demoTag}</MediaTag>}
      </ProductMedia>
      <span className={styles.name}>{product.name}</span>
      <span className={styles.meta}>
        <Chip tone={product.direction} appearance="text">
          {DIRECTION_LABELS[product.direction]}
          {occasion ? ` · ${OCCASION_LABELS[occasion]}` : ''}
        </Chip>
        {product.price ? (
          <span className={styles.price}>{formatMoney(product.price)}</span>
        ) : (
          <span className={styles.unpriced}>[цена не назначена]</span>
        )}
      </span>
    </Link>
  );
}
